import { Input, Space, Typography } from 'antd'
import { CIMSModal } from 'components/CIMSModal'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import { HelpMessage } from 'utils/constants'
import { simpleEditNotification } from 'hooks/notificationHandler'
import CIMSFormItem from 'components/CIMSFormItem'
import { useProjectSettingsContext } from '../../../ProjectSettingsContext'
import CIMSSelect from 'components/CIMSSelect'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { useState } from 'react'
import CIMSLink from 'components/CIMSLink'
import { useProjectContext } from 'context/ProjectContext'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { CASPacketAttachmentOrder } from '@cims-online/shared'
import {
  CASPacketAttachmentOrderDefaultName,
  CustomDriveDefaultName,
} from '../defaults'

export const EditSharePointModal: React.FC<CIMSModalWrapperProps> = ({
  onClose,
  visible,
}) => {
  const { projectId, refetch: refetchProjectContext } = useProjectContext()
  const {
    metadata,
    editMetadata,
    sharePointDrives,
    sharePointDrive,
    fetching,
    refetch,
  } = useProjectSettingsContext()
  const help = HelpMessage.Administration.ProjectSettings.SharePoint
  const [showWarning, setShowWarning] = useState(false)
  const close = () => {
    setShowWarning(false)
    onClose()
  }
  const casPacketAttachmentOrderOptions = useOptions({
    items: CASPacketAttachmentOrder.values,
    value: 'id',
    label: 'name',
    sort: false,
  })
  return (
    <CIMSModal
      title={'Edit SharePoint Settings'}
      onCancel={close}
      onOk={async ({
        sharePointSiteLandingPage,
        customDrive,
        casPacketAttachmentOrderId,
      }) => {
        if (
          sharePointSiteLandingPage !== metadata?.sharePointSiteLandingPage ||
          (customDrive ? customDrive.value : customDrive) !==
            metadata?.customDriveId ||
          casPacketAttachmentOrderId !== metadata?.casPacketAttachmentOrderId
        ) {
          await editMetadata({
            variables: {
              editProjectMetadataInput: {
                projectId,
                sharePointSiteLandingPage: sharePointSiteLandingPage ?? null,
                customDriveId: (customDrive?.value as string) ?? null,
                casPacketAttachmentOrderId: casPacketAttachmentOrderId ?? null,
              },
            },
          })
          simpleEditNotification('SharePoint Settings')
          refetch()
          refetchProjectContext()
        }
        close()
      }}
      visible={visible}
    >
      <CIMSFormItem
        label="SharePoint Landing Page"
        name="sharePointSiteLandingPage"
        help={help.LandingPage}
        initialValue={metadata?.sharePointSiteLandingPage}
      >
        <Input allowClear />
      </CIMSFormItem>
      <CIMSFormItem
        label="Custom Document Library"
        name="customDrive"
        help={help.CustomDrive}
        initialValue={
          metadata?.customDriveId
            ? ({
                label: sharePointDrive?.name ?? 'Unknown Drive',
                value: sharePointDrive?.id ?? metadata.customDriveId,
                tooltip: sharePointDrive?.description,
                deleted: !sharePointDrive,
              } as LabeledValueType)
            : metadata?.customDriveId
        }
        description={
          !showWarning ? undefined : (
            <Space direction="vertical">
              <Typography.Text type="warning">
                Changing the Document Library will not migrate files from the
                previous library to the new one, it only changes where future
                files will be uploaded.
              </Typography.Text>
              <Typography.Text type="secondary">
                You can attempt to manually migrate to the new library by{' '}
                <CIMSLink
                  external
                  href="https://support.microsoft.com/en-us/office/move-or-copy-files-in-sharepoint-00e2f483-4df3-46be-a861-1f5f0c1a87bc"
                >
                  moving
                </CIMSLink>
                , not copying, each folder from the previous library to the new
                one.
              </Typography.Text>{' '}
            </Space>
          )
        }
      >
        <CIMSSelect
          onChange={() => setShowWarning(true)}
          loading={fetching}
          options={sharePointDrives}
          placeholder={CustomDriveDefaultName}
        />
      </CIMSFormItem>
      <CIMSFormItem
        label="CAS Packet Attachment Order"
        name="casPacketAttachmentOrderId"
        help={help.CasPacketAttachmentOrder}
        initialValue={metadata?.casPacketAttachmentOrderId}
      >
        <CIMSSelect
          simple
          options={casPacketAttachmentOrderOptions}
          placeholder={CASPacketAttachmentOrderDefaultName}
        />
      </CIMSFormItem>
    </CIMSModal>
  )
}
